import * as React from "react";
import { useSelector } from "react-redux";
import {
    booleanValueRenderer,
    columnWidth,
    customValueRenderer,
    dateFilterColumn,
    dropDownColumn,
    freeTextColumn,
    multiSelectColumn,
    nonFilterableColumn,
} from "../../components/DataTable";
import FileImporterDialog from "../../components/FileImporterDialog";
import { useCallback, useMemo, useRef, useState } from "react";
import { Add, FileUpload } from "@mui/icons-material";
import {
    BooleanField,
    CurrencyField,
    DateField,
    DateRangeField,
    DecimalField,
    FloatField,
    IndexField,
    MultipleSecuritiesField,
    MultipleSelectField,
    MultipleSourcesField,
    SecurityField,
    SelectField,
    TextField,
} from "../../components/Form";
import { getSecurityName } from "../../store/securities";
import QueryDataTable from "../../components/QueryDataTable";
import { IconButton, Tooltip } from "@mui/material";
import {
    AddRecordDialog,
    EditRecordDialog,
} from "../../components/RecordDialog";
import { FutureActionsTables } from "../../components/FutureActions";
import { CA_EVENT_TYPE } from "../../modules/actionTypes";
import Authentication from "../../modules/authentication";

const gApiClient = Authentication.getAPIClient();

// CA type select options, ordered alphabetically by label
const CA_EVENT_TYPE_OPTIONS = Object.keys(CA_EVENT_TYPE)
    .map((k) => ({ id: k, label: CA_EVENT_TYPE[k] }))
    .sort((a, b) => a.label.localeCompare(b.label));

// CA types that require a price adjustment / share adjustment / linked security
// (see CORPORATE_ACTION_MANDATORY_PARAMS in the backend)
const PRICE_ADJUSTMENT_EVENT_TYPES = [
    "cash_dividend",
    "special_price",
    "override_dmd_price",
    "cash_dividend_with_stock_alternative",
    "stock_dividend_with_cash_alternative",
    "special_dividend",
    "cash_coupon",
    "capital_repayment",
    "rights_issue",
    "rights_issue_price_only",
    "priority_issue",
    "delisting",
    "new_stock_distribution",
];
const SHARE_ADJUSTMENT_EVENT_TYPES = [
    "share_split",
    "stock_dividend",
    "share_consolidation",
    "capital_reduction",
    "scrip_issue",
    "bonus_issue",
    "capital_repayment",
    "rights_issue",
    "rights_issue_price_only",
    "priority_issue",
    "delisting",
    "demerger",
    "merger",
    "new_stock_distribution",
];
const LINKED_SECURITY_EVENT_TYPES = [
    "demerger",
    "merger",
    "new_stock_distribution",
];
// A merger's linked security is expected to have one of these CAs
const DELISTING_EVENT_TYPES = ["delisting", "delisting_at_close_price"];

// Returns a `required` callback - field is mandatory only if the selected CA type is one of `eventTypes`
// (event type is either the selected option or, before the select input converted it, its raw id)
const requiredForEventTypes = (eventTypes) => (values) =>
    eventTypes.includes(values.event_type?.id ?? values.event_type);

const ADD_EDIT_FIELDS = [
    {
        name: "event_type",
        label: "CA Type",
        type: SelectField,
        options: CA_EVENT_TYPE_OPTIONS,
        required: true,
    },
    {
        name: "security_id",
        label: "Security",
        type: SecurityField,
        required: true,
    },
    {
        name: "announcement_date",
        label: "Announcement Date",
        type: DateField,
        required: true,
    },
    {
        name: "execution_date",
        label: "Execution Date",
        type: DateField,
        required: true,
    },
    {
        name: "share_adjustment",
        label: "Share Adjustment",
        type: FloatField,
        required: requiredForEventTypes(SHARE_ADJUSTMENT_EVENT_TYPES),
    },
    {
        name: "price_adjustment",
        label: "Price Adjustment",
        type: DecimalField,
        required: requiredForEventTypes(PRICE_ADJUSTMENT_EVENT_TYPES),
    },
    {
        name: "currency",
        label: "Currency",
        type: CurrencyField,
        required: true,
    },
    {
        name: "linked_security_id",
        label: "Linked Security",
        type: SecurityField,
        required: requiredForEventTypes(LINKED_SECURITY_EVENT_TYPES),
    },
    {
        name: "index_id",
        label: "Index",
        type: IndexField,
        allowEmpty: true,
    },
    {
        name: "apply_dividend_tax",
        label: "Apply Dividend Tax?",
        type: BooleanField,
    },
    {
        name: "note",
        label: "Note",
        type: TextField,
    },
];

// When adding a new CA, dividend tax is applied by default (when editing, the field shows the CA's current value)
const ADD_FIELDS = ADD_EDIT_FIELDS.map((f) =>
    f.name === "apply_dividend_tax" ? { ...f, defaultValue: true } : f,
);

// The file delists securities that have CAs/IAs dated after the delisting - user decides whether to trash them
const IMPORT_CONFIRMATION = {
    key: "future_actions",
    text:
        "The file delists securities that have corporate/index actions dated after the delisting, so nothing " +
        "was imported. Moving them to trash will set their execution/effective date to 01/01/2500.",
    renderRows: (rows) => <FutureActionsTables actions={rows} />,
    actions: [
        {
            text: "Move to Trash and Import",
            color: "warning",
            params: { trash_future_actions: true },
        },
        {
            text: "Import without Moving",
            params: { trash_future_actions: false },
        },
    ],
    confirmedKey: "trashed_actions",
    confirmedText: "Actions Moved to Trash",
};

export default function CorporateActions() {
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editRowIndex, setEditRowIndex] = useState(0);
    const [corporateActions, setCorporateActions] = useState([]);
    // Add delisting CA dialog, opened from the merger's missing delisting warning - { fields, resolve } (resolved
    // with true if the delisting CA was added)
    const [delistingDialog, setDelistingDialog] = useState(null);
    const securitiesById = useSelector(
        (state) => state.securities.securitiesById,
    );
    const indexesById = useSelector((state) => state.indexes.indexesById);
    const queryDataTable = useRef();

    const TABLE_OPTIONS = useMemo(() => {
        return {
            sortOrder: {
                name: "execution_date",
                direction: "asc",
            },
            customIcons: [
                {
                    title: "Add New CA",
                    icon: <Add />,
                    onClick: () => {
                        setShowAddDialog(true);
                    },
                },
            ],
            onEditRow: (index) => {
                setEditRowIndex(index);
                setShowEditDialog(true);
            },
        };
    }, []);

    const COLUMNS = useMemo(() => {
        return [
            freeTextColumn(
                "security_id",
                "Security",
                customValueRenderer((x) => getSecurityName(securitiesById[x])),
            ),
            multiSelectColumn(
                "event_type",
                "CA Type",
                customValueRenderer((x) => CA_EVENT_TYPE[x]),
            ),
            dateFilterColumn("announcement_date", "Announcement Date"),
            dateFilterColumn("execution_date", "Execution Date"),
            nonFilterableColumn(
                "share_adjustment",
                "Share Adjustment",
                columnWidth("100px"),
            ),
            nonFilterableColumn(
                "price_adjustment",
                "Price Adjustment",
                columnWidth("100px"),
            ),
            multiSelectColumn("currency", "Currency", columnWidth("50px")),
            freeTextColumn(
                "linked_security_id",
                "Linked Security",
                customValueRenderer((x) =>
                    x ? getSecurityName(securitiesById[x]) : "",
                ),
            ),
            multiSelectColumn("index_internal_id", "Index ID"),
            dropDownColumn("apply_dividend_tax", "Apply Dividend Tax", {
                ...booleanValueRenderer(),
                ...columnWidth("50px"),
            }),
            dropDownColumn("applied", "Applied", {
                ...booleanValueRenderer(),
                ...columnWidth("50px"),
            }),
            dropDownColumn("is_disabled", "Disabled", {
                ...booleanValueRenderer(),
                ...columnWidth("50px"),
            }),
            nonFilterableColumn("note", "Note"),
        ];
    }, [securitiesById]);

    const QUERY_FIELDS = [
        {
            name: "date_range",
            label: "Execution Date",
            type: DateRangeField,
        },
        {
            name: "securities",
            label: "Securities",
            type: MultipleSecuritiesField,
        },
        {
            name: "linked_securities",
            label: "Linked Securities",
            type: MultipleSecuritiesField,
        },
        {
            name: "event_types",
            label: "CA Type",
            type: MultipleSelectField,
            options: CA_EVENT_TYPE_OPTIONS,
        },
        {
            name: "sources",
            label: "Source",
            type: MultipleSourcesField,
        },
    ];

    const onFinishedAddingRow = async (newRecord) => {
        // Refresh results to show new record
        queryDataTable.current.refreshResults();
        setShowAddDialog(false);
    };

    // A merger's linked security should already have a delisting CA - if it doesn't, the user is asked to confirm
    // (rarely, it's intended)
    const missingLinkedDelistingWarning = async (values) => {
        const linkedSecurityId = values.linked_security_id;
        if (values.event_type !== "merger" || !linkedSecurityId) return null;

        const response = await gApiClient.callApi("corporate_actions", {
            method: "GET",
            params: {
                securities: [linkedSecurityId],
                event_types: DELISTING_EVENT_TYPES,
            },
        });
        const delistings = (response.data.results ?? response.data).filter(
            (ca) => !ca.is_disabled,
        );
        if (delistings.length) return null;

        return {
            message:
                `${getSecurityName(securitiesById[linkedSecurityId])} (the linked security) has no delisting CA - ` +
                "a merger's linked security is normally delisted. Add the merger anyway?",
            buttons: [
                {
                    text: "Add Delisting CA",
                    onClick: () => addLinkedDelisting(values),
                },
            ],
        };
    };

    // Opens the add delisting CA dialog, prefilled with the merger's linked security (delisted), the merger's security
    // (as the delisting's linked security) and currency - resolves to true if the delisting CA was added (so the
    // merger can be added after it)
    const addLinkedDelisting = (mergerValues) => {
        const prefilledValues = {
            event_type: "delisting",
            security_id: mergerValues.linked_security_id,
            linked_security_id: mergerValues.security_id,
            currency: mergerValues.currency,
        };
        const fields = ADD_FIELDS.map((f) =>
            f.name in prefilledValues
                ? { ...f, defaultValue: prefilledValues[f.name] }
                : f,
        );
        return new Promise((resolve) => setDelistingDialog({ fields, resolve }));
    };

    const closeDelistingDialog = (added) => {
        delistingDialog.resolve(added);
        setDelistingDialog(null);
    };

    const onFinishedAddingDelisting = () => {
        // Refresh results to show the new delisting CA (even if the merger isn't added after all)
        queryDataTable.current.refreshResults();
        closeDelistingDialog(true);
    };

    const onFinishedEditingRow = async (updatedRecord) => {
        // Refresh results to show latest updated record
        queryDataTable.current.refreshResults();
        setShowEditDialog(false);
    };

    const enrichResults = useCallback(
        (results) => {
            return results.map((x) => ({
                ...x,
                index_internal_id: x.index_id
                    ? indexesById[x.index_id].internal_id
                    : "",
            }));
        },
        [indexesById],
    );

    return (
        <>
            <AddRecordDialog
                title="Add CA"
                show={showAddDialog}
                fields={ADD_FIELDS}
                submitActions={[
                    {
                        text: "Add",
                        confirmMessage: missingLinkedDelistingWarning,
                        onSubmit: (values, extraBody) =>
                            gApiClient.callApi("corporate_action", {
                                method: "POST",
                                body: { ...values, ...extraBody },
                            }),
                    },
                ]}
                onCancelled={() => setShowAddDialog(false)}
                onFinished={onFinishedAddingRow}
            />
            <AddRecordDialog
                title="Add Delisting CA"
                show={delistingDialog !== null}
                fields={delistingDialog?.fields ?? ADD_FIELDS}
                createAPIPath={"corporate_action"}
                onCancelled={() => closeDelistingDialog(false)}
                onFinished={onFinishedAddingDelisting}
            />
            <EditRecordDialog
                title="Edit CA"
                show={showEditDialog}
                fields={ADD_EDIT_FIELDS}
                record={corporateActions[editRowIndex]}
                updateAPIPath={"corporate_action"}
                createAPIPath={"corporate_action"}
                confirmButtonText="Save Edit"
                needsRecalculationApproval={true}
                onCancelled={() => setShowEditDialog(false)}
                onFinished={onFinishedEditingRow}
            />
            <FileImporterDialog
                title="Bulk Import Corporate Actions"
                importsText="Corporate Actions Imported"
                show={showImportDialog}
                uploadAPIPath={"import_corporate_actions"}
                importsTableColumns={COLUMNS}
                importsTableOptions={TABLE_OPTIONS}
                confirmation={IMPORT_CONFIRMATION}
                onCancelled={() => setShowImportDialog(false)}
                onFinished={() => setShowImportDialog(false)}
            />
            <QueryDataTable
                actions={
                    <Tooltip title={"Bulk Import CAs"}>
                        <IconButton onClick={() => setShowImportDialog(true)}>
                            <FileUpload />
                        </IconButton>
                    </Tooltip>
                }
                ref={queryDataTable}
                tableColumns={COLUMNS}
                tableOptions={TABLE_OPTIONS}
                queryFields={QUERY_FIELDS}
                queryAPIPath="corporate_actions"
                dynamicLoad={true}
                onResults={setCorporateActions}
                preprocessResults={enrichResults}
            />
        </>
    );
}
